import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import InputGroup from "react-bootstrap/InputGroup";
import sanitizeHtml from "sanitize-html";
import be from "../../../BE";
import auth from "../../../Account/Auth";
import "./editor.css";
//react-simple-wysiwyg components
import {
  Editor,
  EditorProvider,
  Toolbar,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnStrikeThrough,
  BtnBulletList,
  BtnNumberedList,
} from "react-simple-wysiwyg";

// max length for editor
const MAX_LENGTH = 800;

// sanitize config. can tweak allowed tags, attributes, styles, etc.
const sanitizeOptions = {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat([
    "u",
    "span",
    "p",
    "b",
    "i",
    "em",
    "strong",
    "ul",
    "ol",
    "li",
    "br",
    "strike",
  ]),
};

function sanitizeAndClamp(html) {
  const clean = sanitizeHtml(html, sanitizeOptions);
  return clean.length > MAX_LENGTH ? clean.slice(0, MAX_LENGTH) : clean;
}

function WelcomeSentence() {
  const { t } = useTranslation(["settings"]);
  const [welcomeSentence, setWelcomeSentence] = useState("");
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingInfo(true);

        await auth
          .isAuthenticated()
          .then(async () => {
            await be
              .get(
                "account-jubilant",
                "/account/settings/sentence",
                {},
                true,
                true
              )
              .then((response) => {
                console.info(
                  "[settings][welcome-sentence] Successfully fetched sentence"
                );
                const fromServer = response?.status || "";
                setWelcomeSentence(fromServer);
              })
              .catch((error) => {
                console.error(
                  "[settings][welcome-sentence] Network error while fetching sentence:",
                  error
                );
              });
          })
          .catch((error) => {
            console.error(
              "[settings][welcome-sentence] Authentication error while fetching sentence:",
              error
            );
          });
      } catch (error) {
        console.error(
          "[settings][welcome-sentence] Functional error while fetching sentence:",
          error
        );
      } finally {
        setLoadingInfo(false);
      }
    };
    fetchData();
  }, []);

  // Basic validation - always returns true here, but we can add checks
  const isValidWelcomeSentence = useCallback(() => {
    return true;
  }, []);

  // Handle changes from WYSIWYG
  const handleChange = (e) => {
    setStatus(0);
    const userHtml = e.target.value || "";
    setWelcomeSentence(sanitizeAndClamp(userHtml));
  };

  // Submit to backend
  const handleSubmit = async (e) => {
    e.preventDefault(); // prevent the form from reloading the page

    try {
      setProcessing(true);

      await auth
        .isAuthenticated()
        .then(async () => {
          await be
            .post(
              "account-jubilant",
              "/account/settings/sentence",
              { sentence: welcomeSentence },
              true,
              true
            )
            .then((response) => {
              console.info(
                "[settings][welcome-sentence] Sentence successfully updated"
              );
              setStatus(200);
            })
            .catch((error) => {
              console.error(
                "[settings][welcome-sentence] Network error while saving sentence:",
                error
              );
              setStatus(error?.response?.status || 500);
            });
        })
        .catch((error) => {
          console.error(
            "[settings][welcome-sentence] Authentication error while saving sentence:",
            error
          );
        });
    } catch (error) {
      console.error(
        "[settings][welcome-sentence] Functional error while saving sentence:",
        error
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <h4>{t("bio.title")}</h4>
      <p>{t("bio.subtitle")}</p>

      <EditorProvider>
        <Form
          noValidate
          validated={isValidWelcomeSentence()}
          onSubmit={handleSubmit}
        >
          <InputGroup hasValidation>
            <Editor
              value={loadingInfo ? "Loading..." : welcomeSentence}
              onChange={handleChange}
              disabled={loadingInfo}
              name="welcome-sentence"
              placeholder={t("bio.placeholder")}
              containerProps={{
                style: {
                  width: "100%",
                  minHeight: "150px",
                  border: "1px solid #ccc",
                  padding: "10px",
                  borderRadius: "4px",
                },
              }}
            >
              <Toolbar>
                <BtnBold />
                <BtnItalic />
                <BtnUnderline />
                <BtnStrikeThrough />
                <BtnBulletList />
                <BtnNumberedList />
                <div class="tw-flex tw-ms-auto tw-me-2">
                  <small
                    style={{
                      color: "var(--color-secondary)",
                    }}
                  >
                    {welcomeSentence.length}/{MAX_LENGTH}
                  </small>
                </div>
              </Toolbar>
            </Editor>

            <Form.Control.Feedback type="invalid">
              <span>{t("bio.invalid.message")}</span>
              <br />
              <span>{t("bio.invalid.hint")}</span>
            </Form.Control.Feedback>
          </InputGroup>
          <Button
            className="tw-ms-auto tw-flex"
            disabled={loadingInfo || processing}
            type="submit"
          >
            {processing ? (
              <Spinner size="sm" animation="border" variant="light" />
            ) : (
              <i className="fas fa-long-arrow-alt-right" />
            )}
          </Button>
        </Form>
      </EditorProvider>

      {status > 400 && status <= 500 && (
        <p className="text-danger" style={{ fontSize: 14 }}>
          {t("bio.error")}
        </p>
      )}
      {status === 200 && (
        <p className="text-success" style={{ fontSize: 14 }}>
          {t("bio.success")}
        </p>
      )}
    </>
  );
}

export default WelcomeSentence;
